import {
  type InvocationQueueStats,
  queuedCount,
  runningCount,
} from "./ActionRecord";

/**
 * How long a newly submitted invocation would wait before it starts.
 *
 * `"starts-now"` is a known zero, not a missing answer: nothing is queued and a slot is free.
 * `"unknown"` means the inputs cannot support an estimate, and a caller should show the queue
 * depth alone rather than invent a number.
 */
export type WaitEstimate =
  | { kind: "estimated"; seconds: number }
  | { kind: "starts-now" }
  | { kind: "unknown" };

/**
 * Rough time to drain the invocations ahead of a newly submitted one.
 *
 * The queued work is priced per action when the server supplies `queued_work_seconds` (each
 * queued invocation at its own action's median runtime), and spread over `concurrency_limit`
 * slots. Anything running now has to finish before that work can start, so it costs one org
 * median on top, charged in full even though an in-flight invocation is on average half done,
 * which makes the result lean high.
 *
 * Without the per-action figure the queue is modelled as waves of `concurrency_limit`, each taking
 * the org median.
 *
 * An empty queue is only a zero wait while a slot is free: an org running at its cap has nothing
 * ahead of the new invocation but nowhere to put it, which the model charges as one org median,
 * the same as any other work in flight.
 *
 * The estimate is "unknown" for an org with no concurrency cap or no completed invocations to take
 * a median from.
 *
 * It assumes the org gets its full concurrency and that recent runtimes predict the next ones,
 * neither of which holds when the fleet is throttled or inputs vary. Present the result coarsely
 * — see {@link formatWaitEstimate}.
 */
export function estimateWait(stats: InvocationQueueStats): WaitEstimate {
  const { concurrency_limit: concurrency, median_duration_seconds: median } =
    stats;

  if (concurrency === null || concurrency <= 0 || median === null) {
    return { kind: "unknown" };
  }

  const ahead = queuedCount(stats);
  const running = runningCount(stats);
  if (ahead === 0 && running < concurrency) {
    return { kind: "starts-now" };
  }

  const inFlight = running > 0 ? median : 0;
  const queuedWork = stats.queued_work_seconds ?? null;
  if (queuedWork !== null) {
    return { kind: "estimated", seconds: queuedWork / concurrency + inFlight };
  }

  return {
    kind: "estimated",
    seconds: Math.ceil(ahead / concurrency) * median + inFlight,
  };
}

/**
 * Render an estimate coarsely. The underlying figure is a model that misses whenever runtimes are
 * uneven, so quoting "43 minutes" would claim precision it doesn't have.
 */
export function formatWaitEstimate(seconds: number): string {
  const minutes = seconds / 60;

  if (minutes < 1) {
    return "under a minute";
  }

  if (minutes < 5) {
    return "a few minutes";
  }

  // Hand off below 60 minutes so this bucket never renders "~60 min".
  if (minutes < 55) {
    return `~${Math.round(minutes / 5) * 5} min`;
  }

  const hours = minutes / 60;
  // Floor, so 23.6 hours cannot round to "~24 hr" against a bucket that says over a day.
  if (hours < 24) {
    const rounded = Math.max(1, Math.floor(hours));
    return `~${rounded} hr`;
  }

  return "over a day";
}
